// Table renderer — plan §6.3 + §6.6 Excel paste (M9-S3).
// Grid + satır ekle/sil + clipboard tab-delimited paste → satır/cell üretir.

import type { QuestionSnapshot } from "@mahir/shared";
import { type ClipboardEvent } from "react";

/** İ8 — sütun tipine göre asgari genişlik (px); metin sütunu en geniş. */
const TABLE_COLUMN_MIN_WIDTH: Record<"text" | "numeric" | "date", number> = {
  text: 200,
  numeric: 110,
  date: 150,
};

interface TableColumn {
  id: string;
  label: string;
  type: "text" | "numeric" | "date";
}

interface Props {
  question: QuestionSnapshot;
  value: Record<string, unknown>[];
  onChange: (v: Record<string, unknown>[]) => void;
}

const MAX_ROWS = 200;

export function TableRenderer({ question, value, onChange }: Props): JSX.Element {
  const columns = (question.options as { columns?: TableColumn[] } | null)?.columns ?? [];
  const rows = value ?? [];

  const addRow = (): void => {
    if (rows.length >= MAX_ROWS) return;
    const blank: Record<string, unknown> = {};
    for (const c of columns) blank[c.id] = c.type === "numeric" ? null : "";
    onChange([...rows, blank]);
  };

  const removeRow = (idx: number): void => {
    const next = rows.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const updateCell = (idx: number, colId: string, v: unknown): void => {
    const next = rows.slice();
    next[idx] = { ...next[idx], [colId]: v };
    onChange(next);
  };

  // M9-S3: Excel/Sheets clipboard paste — `\t` delimited rows.
  // İmlec hangi row/col'da ise oraya yerleşir; taşan kolonlar ignore.
  const handlePaste = (
    e: ClipboardEvent<HTMLTableElement>,
    startRow: number,
    startColIdx: number,
  ): void => {
    const text = e.clipboardData.getData("text");
    if (!text?.includes("\t")) return; // tek hücre yapışması → native fallback
    e.preventDefault();
    const pastedRows = text
      .replace(/\r/g, "")
      .split("\n")
      .filter((r) => r.length > 0);
    if (pastedRows.length === 0) return;
    const next = rows.slice();
    for (let r = 0; r < pastedRows.length; r++) {
      const targetIdx = startRow + r;
      if (targetIdx >= MAX_ROWS) break;
      const cells = pastedRows[r]!.split("\t");
      // Yoksa yeni boş satır doldur
      if (!next[targetIdx]) {
        const blank: Record<string, unknown> = {};
        for (const c of columns) blank[c.id] = c.type === "numeric" ? null : "";
        next[targetIdx] = blank;
      }
      const rowCopy = { ...next[targetIdx] };
      for (let c = 0; c < cells.length; c++) {
        const colTarget = columns[startColIdx + c];
        if (!colTarget) break;
        const raw = cells[c]!.trim();
        if (colTarget.type === "numeric") {
          const n = raw === "" ? null : Number(raw.replace(",", "."));
          rowCopy[colTarget.id] = Number.isNaN(n) ? null : n;
        } else {
          rowCopy[colTarget.id] = raw;
        }
      }
      next[targetIdx] = rowCopy;
    }
    onChange(next);
  };

  if (columns.length === 0) {
    return (
      <div
        className="text-xs px-2 py-1 rounded"
        style={{ background: "var(--color-bg)", color: "var(--color-text-3)" }}
      >
        Bu tablo için kolon tanımlanmamış.
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          overflow: "auto",
          background: "var(--color-surface)",
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}
          onPaste={(e) => {
            // Hangi hücreden paste tetiklendiğini bul: aktif input'un td parent'ının index'i.
            // Boş tablo durumunda (cell yok) 0,0'dan başla — tablo otomatik satır üretilir.
            const target = e.target as HTMLElement;
            const cell = target.closest<HTMLTableCellElement>("td[data-row][data-col]");
            const r = cell ? Number(cell.dataset.row) : 0;
            const c = cell ? Number(cell.dataset.col) : 0;
            handlePaste(e, Number.isFinite(r) ? r : 0, Number.isFinite(c) ? c : 0);
          }}
        >
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.id}
                  style={{
                    // İ8 — sütun içeriğe göre büyür; metin sütunu dar kalmasın (taşarsa
                    // dış kutu yatay kaydırır).
                    minWidth: TABLE_COLUMN_MIN_WIDTH[c.type],
                    textAlign: "left",
                    padding: "8px 12px",
                    borderBottom: "1px solid var(--color-border)",
                    background: "var(--color-bg)",
                    color: "var(--color-text-2)",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  {c.label}
                </th>
              ))}
              <th
                style={{
                  padding: "8px 12px",
                  borderBottom: "1px solid var(--color-border)",
                  background: "var(--color-bg)",
                  width: 40,
                }}
              />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  style={{
                    padding: "12px",
                    color: "var(--color-text-3)",
                    fontSize: "0.82rem",
                  }}
                >
                  Henüz satır yok. &quot;+ Satır Ekle&quot; ile başlayın.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx}>
                  {columns.map((c, colIdx) => (
                    <td
                      key={c.id}
                      data-row={idx}
                      data-col={colIdx}
                      style={{
                        padding: "4px 8px",
                        borderBottom: "1px solid var(--color-border)",
                      }}
                    >
                      <CellInput
                        col={c}
                        value={r[c.id]}
                        onChange={(v) => updateCell(idx, c.id, v)}
                      />
                    </td>
                  ))}
                  <td
                    style={{
                      padding: "4px 8px",
                      borderBottom: "1px solid var(--color-border)",
                      textAlign: "center",
                    }}
                  >
                    <button
                      onClick={() => removeRow(idx)}
                      aria-label="Satırı sil"
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--color-text-3)",
                        fontSize: "1.1rem",
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <button
        onClick={addRow}
        disabled={rows.length >= MAX_ROWS}
        className="mt-2 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50"
        style={{
          background: "transparent",
          border: "1px solid var(--color-border)",
          color: "var(--color-primary)",
        }}
      >
        + Satır Ekle {rows.length >= MAX_ROWS && `(max ${MAX_ROWS})`}
      </button>
    </div>
  );
}

function CellInput({
  col,
  value,
  onChange,
}: {
  col: TableColumn;
  value: unknown;
  onChange: (v: unknown) => void;
}): JSX.Element {
  const common = {
    className: "w-full px-2 py-1 rounded text-sm",
    style: {
      background: "transparent",
      border: "1px solid transparent",
      color: "var(--color-text)",
      fontFamily: col.type === "numeric" ? "var(--font-mono)" : "var(--font-sans)",
    } as const,
  };
  if (col.type === "text") {
    return (
      <input
        type="text"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        {...common}
      />
    );
  }
  if (col.type === "numeric") {
    return (
      <input
        type="number"
        value={typeof value === "number" ? value : ""}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? null : Number(raw));
        }}
        {...common}
      />
    );
  }
  return (
    <input
      type="date"
      value={typeof value === "string" ? value : ""}
      onChange={(e) => onChange(e.target.value || null)}
      {...common}
    />
  );
}
